import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { booleanLookup } from '../../../common/constants/common';
import { IConfigSoftwareMetricState } from './configSoftwareMetric.model';
import {
  deleteConfigSoftwareMetric,
  getConfigSoftwareMetricById,
  saveConfigSoftwareMetric,
  searchConfigSoftwareMetric,
} from './configSoftwareMetric.action';
import { IConfigSoftwareMetric } from '../../../services/master/configSoftwareMetric/configSoftwareMetric.model';

export const initialState: IConfigSoftwareMetricState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const configSoftwareMetricSlice = createSlice({
  name: 'configSoftwareMetric',
  initialState,
  reducers: {
    clearConfigSoftwareMetric: () => {
      return initialState;
    },
    clearConfigSoftwareMetricMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearConfigSoftwareMetricGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchConfigSoftwareMetric.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchConfigSoftwareMetric.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { records, total_count, table_name, column_selection } = action.payload;
      state.search.data = records;
      state.search.count = total_count;
      state.search.lookups = { booleanLookup };
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = table_name;
      if (column_selection) {
        state.tableColumnSelection.id = column_selection.id;
        const tableSelectionObj = JSON.parse(column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = table_name;
    },
    [searchConfigSoftwareMetric.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getConfigSoftwareMetricById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getConfigSoftwareMetricById.fulfilled.type]: (
      state,
      action: PayloadAction<IConfigSoftwareMetric>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getConfigSoftwareMetricById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveConfigSoftwareMetric.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveConfigSoftwareMetric.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveConfigSoftwareMetric.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteConfigSoftwareMetric.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteConfigSoftwareMetric.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteConfigSoftwareMetric.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const configSoftwareMetricSelector = (state: RootState) => state.configSoftwareMetric;

// Actions
export const {
  clearConfigSoftwareMetric,
  clearConfigSoftwareMetricMessages,
  clearConfigSoftwareMetricGetById,
  setTableColumnSelection,
} = configSoftwareMetricSlice.actions;

// The reducer
export default configSoftwareMetricSlice.reducer;
